"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowUp, Facebook, Instagram, Youtube } from "lucide-react";
import { useTranslation } from "@/contexts/TranslationContext";

interface FooterProps {
  variant?: "full" | "simple";
}

export default function Footer({ variant = "full" }: FooterProps) {
  const { t } = useTranslation();
  const [footerLogo, setFooterLogo] = useState<string | null>(null);
  const [secondaryColor, setSecondaryColor] = useState("#1f2937");
  const [companyName, setCompanyName] = useState("MACH9 Treinamentos");
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [companyFacebook, setCompanyFacebook] = useState("");
  const [companyInstagram, setCompanyInstagram] = useState("");
  const [companyYoutube, setCompanyYoutube] = useState("");
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "http://localhost:5000";
        const response = await fetch(`${backendUrl}/api/website/public/logos`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success) {
          if (data.data?.footer_logo) {
            setFooterLogo(data.data.footer_logo);
          }
          if (data.data?.secondary_color) {
            setSecondaryColor(data.data.secondary_color);
          }
          if (data.data?.company_name) {
            setCompanyName(data.data.company_name);
          }
          if (data.data?.company_email) {
            setCompanyEmail(data.data.company_email);
          }
          if (data.data?.company_phone) {
            setCompanyPhone(data.data.company_phone);
          }
          if (data.data?.social_facebook) {
            setCompanyFacebook(data.data.social_facebook);
          }
          if (data.data?.social_instagram) {
            setCompanyInstagram(data.data.social_instagram);
          }
          if (data.data?.social_youtube) {
            setCompanyYoutube(data.data.social_youtube);
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.warn("Backend não disponível para logo do footer");
        }
      }
    };

    fetchFooterData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (variant === "simple") {
    return (
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm">
            &copy; {new Date().getFullYear()} {companyName}. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-gray-900 text-gray-400 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center h-10 mb-4">
              {footerLogo && (
                <Image
                  src={footerLogo}
                  alt="Logo"
                  width={120}
                  height={40}
                  className="h-10 w-auto object-contain"
                  unoptimized
                />
              )}
            </div>
            <p className="text-sm">
              {t("footer.description")}
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4">{t("footer.quickLinks")}</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link href="/cursos" className="hover:text-white transition">
                  {t("footer.courses")}
                </Link>
              </li>
              <li>
                <Link href="/verificar" className="hover:text-white transition">
                  {t("footer.verifyCertificate")}
                </Link>
              </li>
              <li>
                <Link href="/sobre" className="hover:text-white transition">
                  {t("footer.aboutUs")}
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4">{t("footer.support")}</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link href="/faq" className="hover:text-white transition">
                  {t("footer.faq")}
                </Link>
              </li>
              <li>
                <Link href="/contato" className="hover:text-white transition">
                  {t("footer.contact")}
                </Link>
              </li>
              <li>
                <Link href="/termos" className="hover:text-white transition">
                  Termos de Uso
                </Link>
              </li>
              <li>
                <Link href="/privacidade" className="hover:text-white transition">
                  {t("footer.privacyPolicy")}
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4">{t("footer.contactInfo")}</h4>
            <ul className="space-y-2 text-sm">
              {companyEmail && (
                <li>
                  <a href={`mailto:${companyEmail}`} className="hover:text-white transition">
                    {companyEmail}
                  </a>
                </li>
              )}
              {companyPhone && (
                <li>
                  <a href={`tel:${companyPhone.replace(/\D/g, "")}`} className="hover:text-white transition">
                    {companyPhone}
                  </a>
                </li>
              )}
              {!companyEmail && !companyPhone && (
                <li className="text-gray-500">Não informado</li>
              )}
            </ul>
            {/* Redes Sociais */}
            {(companyFacebook || companyInstagram || companyYoutube) && (
              <div className="flex items-center gap-4 mt-4">
                {companyFacebook && (
                  <a
                    href={companyFacebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-5 h-5 text-white" />
                  </a>
                )}
                {companyInstagram && (
                  <a
                    href={companyInstagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-800 hover:bg-pink-600 rounded-full flex items-center justify-center transition-colors"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-5 h-5 text-white" />
                  </a>
                )}
                {companyYoutube && (
                  <a
                    href={companyYoutube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-800 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
                    aria-label="YouTube"
                  >
                    <Youtube className="w-5 h-5 text-white" />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm">
          <p>
            &copy; {new Date().getFullYear()} {companyName}. Todos os direitos reservados.
          </p>
        </div>
      </div>

      {/* Botão Voltar ao Topo */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-[190px] md:bottom-24 right-6 w-14 h-14 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 z-40 hover:opacity-90 ${
          showScrollTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        style={{ backgroundColor: secondaryColor }}
        aria-label="Voltar ao topo"
      >
        <ArrowUp className="w-7 h-7" />
      </button>
    </footer>
  );
}
